/**
 * The instant dogfight's debriefing — `/fly` with no mission at all.
 *
 * Split from `debrief.ts` because the two fill the same `MissionDebrief` from
 * genuinely different material: that module reports a `MissionRun` against the
 * briefing it was given, this one has only a bare `EncounterState` and its two
 * combatants. Keeping them apart also keeps either under the size at which a
 * reader stops being able to hold the whole of one in their head.
 */

import { aircraftById, type EncounterState } from "../combat/encounter.ts";
import type { EncounterStatus } from "../combat/outcome.ts";
import { formatDuration, missionScore, type MissionDebrief } from "./debrief.ts";
import { NO_REVIEW_FACTS, reviewNarrative } from "./debrief-review.ts";
import type { MissionOutcome, MissionStats } from "./mission-run.ts";

/**
 * The instant dogfight's debriefing — `/fly` with no mission, a bare
 * `EncounterSetup` rather than a `MissionSpec` (`docs/engineering/replay.md`).
 * There is no `MissionRun` to read a `MissionStats` off, so this builds one
 * directly from the `EncounterState`'s two combatants and reuses the same
 * `missionScore`/`formatDuration` the mission debrief does — the same card,
 * fed from a smaller fight.
 *
 * **Not** routed through `createMissionRun`/`stepMissionRun` with a synthetic
 * one-objective `MissionSpec`, on purpose: that machinery's "success on the
 * same tick still wins" rule (`resolveOutcome`) would call a mutual kill a
 * win, because the objective (destroy the enemy) was still met. A dogfight
 * that goes down with its opponent is not a win, so the outcome is read off
 * `EncounterStatus` directly instead — `enemyDown` is the only `"success"`.
 */
export function dogfightDebrief(
  state: EncounterState,
  playerId: string,
  title = "Instant dogfight",
  /**
   * Names the famous ace, when there is one. `REVIEW.DAT` keeps the duel's five
   * messages in their own eleventh section — *"You shot down the legendary
   * %n!"* — and they are the right ones for a named opponent; a nameless
   * instant dogfight falls back to the DOGFIGHT section's grading.
   */
  aceName?: string,
): MissionDebrief {
  const stats = dogfightStats(state, playerId, aceName);
  const outcome = dogfightOutcome(state.status);
  return {
    missionId: "instant-dogfight",
    title,
    outcome,
    headline: DOGFIGHT_HEADLINES[state.status],
    narrative: dogfightNarrative(state, stats, aceName),
    missionScore: missionScore(stats, outcome),
    aircraftLost: stats.friendlyLosses,
    personalVictories: stats.aircraftKills,
    squadronVictories: stats.squadronKills,
    squadronBalloonVictories: 0,
    squadronZeppelinVictories: 0,
    aircraftKills: stats.aircraftKills,
    balloonKills: 0,
    zeppelinKills: 0,
    roundsFired: stats.roundsFired,
    hitsScored: stats.hitsScored,
    hitsTaken: stats.hitsTaken,
    duration: formatDuration(stats.durationS),
    victories: [],
    friendlyKills: 0,
    groundTargetsDestroyed: 0,
    friendlyGroundTargetsLost: 0,
    playerGroundTargetsDestroyed: 0,
    raid: undefined,
    playerLost: stats.playerStatus === "destroyed",
    playerWounded: stats.playerWounded,
    pilotKilled: stats.pilotKilled,
    overFriendlyTerritory: stats.overFriendlyTerritory,
    landedSafely: stats.landedSafely,
    landedAtBase: stats.landedAtBase,
    gearCollapsed: stats.gearCollapsed,
    landedInEnemyTerritory: stats.landedSafely && !stats.overFriendlyTerritory,
    // AD-021: the bail-out verb is not offered in an instant dogfight or a duel.
    baledOut: stats.baledOut,
    killedBy: stats.killedBy,
    objectives: [],
    summary: dogfightSummary(state.status, stats),
  };
}

/**
 * `enemyDown` is the only win. `playerDown` and `mutualDestruction` both cost
 * the player his own aeroplane, so both read as `"failure"` — a mutual kill is
 * a draw, not a victory, and the debrief card's red "lost" styling
 * (`DebriefOverlay`'s `outcome !== "success"`) is the right call for it.
 */
function dogfightOutcome(status: EncounterStatus): MissionOutcome {
  if (status === "active") return "inProgress";
  return status === "enemyDown" ? "success" : "failure";
}

/**
 * The same three lines the in-flight HUD banner already showed
 * (`OUTCOME_BANNERS` in `apps/web/src/flight/combat-hud.ts`) — carried over
 * verbatim so the debrief's headline agrees with what the pilot just saw.
 */
const DOGFIGHT_HEADLINES: Readonly<Record<EncounterStatus, string>> = {
  active: "",
  enemyDown: "ENEMY DESTROYED",
  playerDown: "YOU'VE BEEN SHOT DOWN",
  mutualDestruction: "YOU WENT DOWN TOGETHER",
};

/**
 * The bare encounter's `REVIEW.DAT` paragraph. There is no `MissionRun` here, so
 * the facts are read straight off the two combatants the same way
 * `dogfightStats` reads the rest — `enemyTotal` is one, because a duel is one
 * opponent, which is what makes "all of the enemy fighters" and "you shot down
 * the legendary %n" mean the same event.
 */
function dogfightNarrative(state: EncounterState, stats: MissionStats, aceName: string | undefined): readonly string[] {
  const enemyDown = state.status === "enemyDown" || state.status === "mutualDestruction";
  return reviewNarrative(aceName ? "aceDuel" : "dogfight", {
    ...NO_REVIEW_FACTS,
    madeContact: stats.roundsFired > 0 || stats.hitsTaken > 0 || enemyDown || stats.playerStatus === "destroyed",
    enemyTotal: 1,
    enemyDestroyed: enemyDown ? 1 : 0,
    friendlyLosses: stats.friendlyLosses,
    playerLost: stats.playerStatus === "destroyed",
    aceName: aceName ?? NO_REVIEW_FACTS.aceName,
  });
}

function dogfightSummary(status: EncounterStatus, stats: MissionStats): string[] {
  const lines: string[] = [];
  if (status === "enemyDown") lines.push("One victory confirmed.");
  else if (status === "mutualDestruction") lines.push("Both aircraft went down.");
  else lines.push("No victories claimed.");
  if (stats.playerWounded) lines.push("You were wounded and will be off flying duty.");
  return lines;
}

/**
 * A `MissionStats` built off the encounter's two combatants instead of a
 * mission's kill tally — everything the dogfight has no concept of (ground
 * targets, a raid, a home aerodrome, squadron mates) is zero, `undefined` or
 * `false`. `roundsFired`/`hitsScored`/`hitsTaken`/landing are read off the
 * player's own `CombatAircraft` record exactly as `combatHudFrom` does.
 */
function dogfightStats(state: EncounterState, playerId: string, aceName?: string): MissionStats {
  const player = aircraftById(state, playerId);
  const enemyDestroyed = state.status === "enemyDown" || state.status === "mutualDestruction";
  const playerDestroyed = player?.status === "destroyed";
  const gear = player?.sim.gear;
  const landedSafely = !playerDestroyed && !player?.sim.crashed && (gear?.stopped ?? false) && !(gear?.collapsed ?? false);
  return {
    ticks: state.tick,
    durationS: state.timeS,
    aircraftKills: enemyDestroyed ? 1 : 0,
    balloonKills: 0,
    zeppelinKills: 0,
    squadronKills: enemyDestroyed ? 1 : 0,
    squadronBalloonKills: 0,
    squadronZeppelinKills: 0,
    friendlyLosses: playerDestroyed ? 1 : 0,
    friendlyKills: 0,
    groundTargetsDestroyed: 0,
    friendlyGroundTargetsLost: 0,
    playerGroundTargetsDestroyed: 0,
    raid: undefined,
    // A duel has exactly one other aeroplane in it, so an attacker that is not
    // the player *is* the opponent — but he only gets named when the port has a
    // name for him, and only when he actually did it. Going into the ground on
    // your own leaves `%a` unfilled and the unnamed `VANQUISHED!` standing.
    killedBy:
      playerDestroyed && player?.lastAttackerId !== null && player?.lastAttackerId !== playerId ? aceName : undefined,
    victories: [],
    roundsFired: player?.roundsFired ?? 0,
    hitsScored: player?.hitsScored ?? 0,
    hitsTaken: player?.hitsTaken ?? 0,
    playerStatus: player?.status ?? "flying",
    playerWounded: player?.sim.pilot.wounded ?? false,
    pilotKilled: player ? !player.sim.pilot.alive : false,
    // No front line in a bare encounter — nothing consumes this for the
    // instant dogfight (there is no career to take a POW), so "friendly" is
    // the harmless default rather than a guess at a line that does not exist.
    overFriendlyTerritory: true,
    everAirborne: true,
    landedSafely,
    landedAtBase: false,
    gearCollapsed: gear?.collapsed ?? false,
    // AD-021. `/duel` does not offer the bail-out verb at all (the design doc's
    // §multiplayer says why), and the instant dogfight has no career to divert,
    // so this is always false here rather than reading `state.parachute`.
    baledOut: false,
  };
}
